import React, { lazy, useLayoutEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import ScrollToTop from 'components/Global/ScrollToTop/ScrollToTop';
import { IPage } from 'interfaces/pages';
import Metadata from 'components/Global/Metadata/Metadata';
import { useMetaData } from 'hooks/useMetaData';
import ZipGateModal from 'components/Modal/ZipGateModal/ZipGateModal';
import { createBrowserHistory } from 'history';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

const SentryRoute = Sentry.withSentryRouting(Route);

const history = createBrowserHistory();

//TODO: Upgrade to react router 6.0.0
const NonReactRaqImport = lazy(
  () =>
    import(
      /* webpackChunkName: "non-react-raq" */ 'pages/NonReactRaqModal/NonReactRaqModal'
    )
);

const NonReactImport = lazy(
  () =>
    import(
      /* webpackChunkName: "non-react-modal" */ 'pages/NonReactModal/NonReactModal'
    )
);

const NonReactConciergeImport = lazy(
  () =>
    import(
      /* webpackChunkName: "non-react-concierge" */ 'pages/Concierge/Concierge'
    )
);

const NonReactConciergeEmbedChatImport = lazy(
  () =>
    import(
      /* webpackChunkName: "non-react-concierge-embed" */ 'pages/Concierge/ConciergeEmbedChat'
    )
);

const NonReactConciergeAiEmbedChatImport = lazy(
  () =>
    import(
      /* webpackChunkName: "non-react-concierge-ai-embed" */ 'pages/Concierge/ConciergeAiEmbedChat'
    )
);

const BuildAndPriceImport = lazy(
  () =>
    import(
      /* webpackChunkName: "build-price" */ 'pages/BuildAndPrice/BuildAndPrice'
    )
);
const PackageImport = lazy(
  () =>
    import(
      /* webpackChunkName: "package-selector" */ 'pages/PackageSelector/PackageSelector'
    )
);
const PowertrainImport = lazy(
  () =>
    import(
      /* webpackChunkName: "powertrain-selector" */ 'pages/PowertrainSelector/PowertrainSelector'
    )
);
const ThemeComponent = lazy(
  () =>
    import(/* webpackChunkName: "style-guide" */ 'pages/StyleGuide/StyleGuide')
);

const LocalHomepageImport = lazy(
  () =>
    import(
      /* webpackChunkName: "estimate-payments" */ 'pages/LocalHomepage/LocalHomepage'
    )
);

const RequestAQuoteImport = lazy(
  () =>
    import(
      /* webpackChunkName: "request-a-quote" */ 'pages/RequestAQuote/RequestAQuote'
    )
);

const DealerLocatorImport = lazy(
  () =>
    import(
      /* webpackChunkName: "locate-a-dealer" */ 'pages/DealerLocator/DealerLocator'
    )
);
const ReserveImport = lazy(
  () => import(/* webpackChunkName: "reserve-online" */ 'pages/Reserve/Reserve')
);

const ConfirmationImport = lazy(
  () =>
    import(
      /* webpackChunkName: "payment-confirmation" */ 'pages/PaymentConfirmation/PaymentConfirmation'
    )
);

const InventoryRAQImport = lazy(
  () =>
    import(
      /* webpackChunkName: "inventory-results-raq" */ 'pages/InventoryRAQ/InventoryRAQ'
    )
);

const InventoryResultsImport = lazy(
  () =>
    import(
      /* webpackChunkName: "inventory-results" */ 'pages/InventoryResults/InventoryResults'
    )
);

const OffersImport = lazy(
  () => import(/* webpackChunkName: "Offers" */ 'pages/Offers/Offers')
);

const PaymentEstimatorImport = lazy(
  () =>
    import(
      /* webpackChunkName: "payment-estimator" */ 'components/EstimatePayments/PaymentEstimatorRaq/PaymentEstimatorRaq'
    )
);

const PricingAndSpecsImport = lazy(
  () =>
    import(
      /* webpackChunkName: "pricing-specs" */ 'pages/PricingAndSpecs/PricingAndSpecs'
    )
);

const InventoryDetailsImport = lazy(
  () =>
    import(
      /* webpackChunkName: "inventory-details" */ 'pages/InventoryDetails/InventoryDetails'
    )
);

const EstimatePaymentsImport = lazy(
  () =>
    import(
      /* webpackChunkName: "estimate-payments" */ 'pages/EstimatePayments/EstimatePayments'
    )
);

const AppointmentsTekionImport = lazy(
  () =>
    import(
      /* webpackChunkName: "appointments-tekion" */ 'pages/AppointmentsTekion/AppointmentsTekion'
    )
);

const OrdersTekionImport = lazy(
  () =>
    import(
      /* webpackChunkName: "orders-tekion" */ 'pages/OrdersTekion/OrdersTekion'
    )
);

const ADXEnhancedImport = lazy(
  () =>
    import(
      /* webpackChunkName: "adx-enhanced" */ 'pages/ADXEnhanced/ADXEnhanced'
    )
);

// Sentry initializer, would be integrated with react router
if (window.Acr?.sentryConfig?.dsn) {
  Sentry.init({
    ...window.Acr.sentryConfig,
    beforeSendTransaction(event) {
      event.transaction = event.transaction?.toLowerCase();
      return event;
    },
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
        tracePropagationTargets: ['localhost', /^\//],
      }),
    ],
  });
}

const PageRouting = () => {
  const { data } = useMetaData();
  const url = window.location.href;
  const optionQuery = document.querySelectorAll(
    "[israqmodal='true'], [isgeneralmodal='true'], [isconciergeaiembedchat='true']"
  );
  const isOldInventory = document.querySelectorAll('.inv-body');

  useLayoutEffect(() => {
    document.body.classList.add('acr-focus-disabled');
  }, []);

  const getComponent = (page: IPage) => {
    switch (page.type) {
      case 'powertrain':
        return <PowertrainImport />;
      case 'package':
        return <PackageImport />;
      case 'buildandprice':
        return <BuildAndPriceImport />;
      case 'requestaquote':
        return <RequestAQuoteImport />;
      case 'dealerlocator':
        return <DealerLocatorImport />;
      case 'reserve':
        return <ReserveImport />;
      case 'confirmation':
        return <ConfirmationImport />;
      case 'inventoryresults':
        return isOldInventory.length ? (
          <InventoryRAQImport />
        ) : (
          <InventoryResultsImport />
        );
      case 'inventorydetails':
        return isOldInventory.length ? (
          <InventoryRAQImport />
        ) : (
          <InventoryDetailsImport />
        );
      case 'offers':
        return <OffersImport />;
      case 'appointmentstekion':
        return <AppointmentsTekionImport />;
      case 'orderstekion':
        return <OrdersTekionImport />;
      case 'paymentestimator':
        return <PaymentEstimatorImport />;
      case 'pricingandspecs':
        return <PricingAndSpecsImport />;
      case 'estimatepayments':
        return <EstimatePaymentsImport />;
      case 'adxenhanced':
        return <ADXEnhancedImport />;
      default:
        return;
    }
  };

  if (data && optionQuery.length === 0) {
    return (
      <>
        <ScrollToTop />
        <ZipGateModal
          labels={data.pageContent.labels}
          mapBackground={data.pageContent.images.zipGateBackground}
        />
        <Switch>
          {data.pageMetadata.pages.map((page) => {
            return page.type ? (
              <SentryRoute
                exact
                path={`${page.path.replace(/\/\s*$/, '')}${
                  page.type === 'buildandprice' ? '/:vehicleModelSeries' : ''
                }`}
                key={`route${page.id}`}
              >
                <Metadata
                  metadata={page.metadata}
                  canonicalUrls={data.pageMetadata.canonicalUrls}
                />
                {getComponent(page)}
              </SentryRoute>
            ) : null;
          })}
          <SentryRoute exact path="/style-guide">
            <ThemeComponent />
          </SentryRoute>
          <SentryRoute exact path="/testing-pages/estimate-payments">
            <EstimatePaymentsImport />
          </SentryRoute>
          <SentryRoute exact path="/ADXEnhanced">
            <ADXEnhancedImport />;
          </SentryRoute>
          {url.includes('localhost') || url.includes('127.0.0.1') ? (
            <SentryRoute exact path="/">
              <LocalHomepageImport />
            </SentryRoute>
          ) : (
            <SentryRoute exact path="/">
              <h1>NOT FOUND</h1>
            </SentryRoute>
          )}
          <SentryRoute exact path="/">
            <h1>NOT FOUND</h1>
          </SentryRoute>
          <SentryRoute path="*">
            <h1>NOT FOUND</h1>
          </SentryRoute>
        </Switch>
      </>
    );
  } else if (optionQuery.length !== 0) {
    const raqOption = document.querySelector("[israqmodal='true']");
    const generalOption = document.querySelector("[isgeneralmodal='true']");
    const conciergeAiEmbedChat = document.querySelector(
      "[isconciergeaiembedchat='true']"
    );
    return (
      <>
        {raqOption && <NonReactRaqImport />}
        {generalOption && <NonReactImport />}
        {conciergeAiEmbedChat && <NonReactConciergeAiEmbedChatImport />}
      </>
    );
  }
  return <></>;
};

export default PageRouting;
